import { useState } from 'react';
import { RefreshCw, ChevronDown, Sun, Moon, Share2 } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { handleTabKeyDown } from '../utils/tabKeyboard';
import { useNavigate } from 'react-router-dom';
import { SeasonDropdown } from './SeasonDropdown';
import { useTheme } from '../contexts/ThemeContext';
import { useLeagueOptional } from '../contexts/LeagueContext';
import { ShareLeagueDialog } from './shared/ShareLeagueDialog';
import { MoreMenu } from './MoreMenu';

export function HeaderStrip({ tabs, activeTab, onTabChange, leagueName, headMeta, isLoading, onRefresh, onOpenSheet, leagueId, provider, activeSeason, viewer = false, api = null }) {
  const { user } = useUser();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  // Home renders this header with no LeagueProvider.
  const shareToken = useLeagueOptional()?.shareToken ?? null;
  const [shareOpen, setShareOpen] = useState(false);
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const nextThemeLabel = nextTheme === 'dark' ? 'Dark' : 'Light';

  const avatar = user?.imageUrl ? (
    <div style={{ width: '30px', height: '30px', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
      <img src={user.imageUrl} alt="account" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </div>
  ) : (
    <div style={{ width: '30px', height: '30px', borderRadius: '6px', background: 'var(--you)', color: 'var(--you-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>
      {(user?.firstName?.[0] || '?').toUpperCase()}
    </div>
  );

  const themeToggle = (
    <button
      type="button"
      className="ledger-btn"
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
      onClick={toggleTheme}
    >
      {nextTheme === 'dark'
        ? <Moon aria-hidden="true" style={{ width: '15px', height: '15px' }} />
        : <Sun aria-hidden="true" style={{ width: '15px', height: '15px' }} />}
      <span className="hidden sm:inline">{nextThemeLabel}</span>
    </button>
  );

  const refreshButton = (
    <button
      type="button"
      onClick={onRefresh}
      disabled={isLoading}
      title="Refresh"
      aria-label="Refresh league data"
      className="ledger-btn"
    >
      <RefreshCw aria-hidden="true" className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
      <span className="hidden sm:inline">Refresh</span>
    </button>
  );

  // Viewers have no member API, so only members get the Share button.
  const shareButton = !viewer && api ? (
    <button type="button" className="ledger-btn" aria-label="Share" onClick={() => setShareOpen(true)}>
      <Share2 aria-hidden="true" className="w-4 h-4" /><span className="hidden sm:inline">Share</span>
    </button>
  ) : null;

  return (
    <header className="ledger-header ledger-scope sticky top-0 z-10">
      <div className="mx-auto px-4 sm:px-6" style={{ maxWidth: '1600px' }}>

        {/* ── Mobile: league pill + theme toggle + avatar ── */}
        <div className="flex sm:hidden items-center justify-between" style={{ height: '52px', gap: '8px' }}>
          {viewer ? (
            <>
              <span style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'left', color: 'var(--ledger-ink)' }}>
                {leagueName || 'Loading...'}
              </span>
              <span className="ledger-tag ledger-tag-muted">Shared view</span>
              <SeasonDropdown leagueId={leagueId} provider={provider} activeSeason={activeSeason} shareToken={shareToken} />
            </>
          ) : (
            <button
              type="button"
              onClick={onOpenSheet}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--ledger-panel)', border: '1px solid var(--ledger-line-2)', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', flex: 1, minWidth: 0, minHeight: '44px', color: 'var(--ledger-ink)' }}
            >
              <span aria-hidden="true" style={{ width: '3px', height: '16px', borderRadius: '1px', background: 'var(--you)', flexShrink: 0 }} />
              <span style={{ fontSize: '14px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
                {leagueName || 'Loading...'}
              </span>
              <ChevronDown aria-hidden="true" style={{ width: '14px', height: '14px', color: 'var(--ledger-muted)', flexShrink: 0 }} />
            </button>
          )}
          <MoreMenu items={[
            ...(!viewer && api ? [{ label: 'Share', icon: <Share2 aria-hidden="true" className="w-4 h-4" />, onSelect: () => setShareOpen(true) }] : []),
            ...(viewer ? [] : [{ label: 'Refresh league data', icon: <RefreshCw aria-hidden="true" className="w-4 h-4" />, onSelect: onRefresh, disabled: isLoading }]),
            { label: `Switch to ${nextTheme} mode`, icon: nextTheme === 'dark' ? <Moon aria-hidden="true" className="w-4 h-4" /> : <Sun aria-hidden="true" className="w-4 h-4" />, onSelect: toggleTheme },
          ]} />
          {viewer ? null : (
            <button type="button" aria-label="Settings" className="ph-account" onClick={() => navigate('/settings', { state: { from: window.location.pathname + window.location.search } })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0 }}>
              {avatar}
            </button>
          )}
        </div>

        {/* ── Desktop: title row (name, season, sync status, tools) over the tab strip ── */}
        <div className="hidden sm:block">
          <div className="flex items-center flex-wrap" style={{ gap: '14px', padding: '14px 0 10px' }}>
            <h1
              style={{
                margin: 0, minWidth: 0, maxWidth: '100%', flexShrink: 1,
                fontSize: '28px', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.1,
                color: 'var(--ledger-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {leagueName || ''}
            </h1>
            {viewer ? <span className="ledger-tag ledger-tag-muted">Shared view</span> : null}
            <SeasonDropdown leagueId={leagueId} provider={provider} activeSeason={activeSeason} shareToken={shareToken} />
            {headMeta && (
              <span style={{ fontSize: '13px', color: 'var(--ledger-muted)' }}>{headMeta}</span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {shareButton}
              {viewer ? null : refreshButton}
              {themeToggle}
            </div>
          </div>
          {tabs.length > 0 && (
            <div
              role="tablist"
              aria-label="Dashboard sections"
              style={{ display: 'flex', gap: '22px', height: '44px' }}
            >
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  id={`dashboard-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls="dashboard-panel"
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  onClick={() => onTabChange(tab.id)}
                  onKeyDown={e => handleTabKeyDown(e, tabs.map(t => t.id), activeTab, onTabChange, id => `dashboard-tab-${id}`)}
                  className="ledger-tab"
                  style={{ position: 'relative' }}
                >
                  {tab.label}
                  {tab.dot && <span className="sr-only">, new</span>}
                  {tab.dot && (
                    <span aria-hidden="true" style={{ position: 'absolute', top: 14, right: -8, width: 6, height: 6, borderRadius: '50%', background: 'var(--you)' }} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

      </div>
      {shareButton ? <ShareLeagueDialog api={api} open={shareOpen} onClose={() => setShareOpen(false)} /> : null}
    </header>
  );
}

export default HeaderStrip;
